import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";

const fineStatusLabel = {
  unpaid: "ค้างชำระ",
  partial: "ชำระบางส่วน",
  paid: "ชำระครบ",
  waived: "ยกเว้นค่าปรับ",
  cancelled: "ยกเลิก"
};

const toDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function LibraryFines() {
  const [loans, setLoans] = useState([]);
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingAssess, setSubmittingAssess] = useState(false);
  const [submittingPay, setSubmittingPay] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [assessForm, setAssessForm] = useState({
    loan_no: "",
    fine_reason: "overdue",
    fine_amount: "",
    waived_amount: "0",
    fine_note: ""
  });

  const [payForm, setPayForm] = useState({
    fine_no: "",
    paid_amount: "",
    waived_amount: "0",
    fine_note: ""
  });

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const payableFines = useMemo(
    () => fines.filter((fine) => ["unpaid", "partial"].includes(fine.fine_status)),
    [fines]
  );

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [loansRes, finesRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getLoans`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getFines`, { headers })
      ]);

      setLoans(loansRes.data || []);
      setFines(finesRes.data || []);
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลค่าปรับได้");
      console.error("LibraryFines fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssessFine = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!assessForm.loan_no || assessForm.fine_amount === "") {
      setError("กรุณาเลือกรายการยืมและระบุจำนวนเงินค่าปรับ");
      return;
    }

    try {
      setSubmittingAssess(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/assessFine`,
        {
          loan_no: assessForm.loan_no,
          fine_reason: assessForm.fine_reason,
          fine_amount: Number(assessForm.fine_amount || 0),
          waived_amount: Number(assessForm.waived_amount || 0),
          fine_note: assessForm.fine_note.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess(`ประเมินค่าปรับสำเร็จ เลขที่: ${response.data?.fine_no || "-"}`);
      setAssessForm({
        loan_no: "",
        fine_reason: "overdue",
        fine_amount: "",
        waived_amount: "0",
        fine_note: ""
      });
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "loan not found") {
        setError("ไม่พบเลขที่รายการยืม");
      } else if (message === "waived amount exceeds fine amount") {
        setError("ยอดยกเว้นต้องไม่มากกว่ายอดค่าปรับ");
      } else {
        setError("ประเมินค่าปรับไม่สำเร็จ");
      }
      console.error("Assess fine error:", submitError);
    } finally {
      setSubmittingAssess(false);
    }
  };

  const handlePayFine = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!payForm.fine_no) {
      setError("กรุณาเลือกรายการค่าปรับ");
      return;
    }

    try {
      setSubmittingPay(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/payFine`,
        {
          fine_no: payForm.fine_no,
          paid_amount: Number(payForm.paid_amount || 0),
          waived_amount: Number(payForm.waived_amount || 0),
          fine_note: payForm.fine_note.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess("บันทึกการรับชำระค่าปรับสำเร็จ");
      setPayForm({ fine_no: "", paid_amount: "", waived_amount: "0", fine_note: "" });
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "payment exceeds fine amount") {
        setError("ยอดชำระรวมมากกว่ายอดค่าปรับ");
      } else if (message === "fine already closed") {
        setError("รายการค่าปรับนี้ปิดรายการแล้ว");
      } else if (message === "missing payment amount") {
        setError("กรุณาระบุยอดชำระหรือยอดยกเว้น");
      } else {
        setError("บันทึกการชำระค่าปรับไม่สำเร็จ");
      }
      console.error("Pay fine error:", submitError);
    } finally {
      setSubmittingPay(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">งานค่าปรับห้องสมุด</h4>
          <Badge bg="danger">Fine</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Card className="h-100">
              <Card.Header className="fw-semibold">ประเมินค่าปรับ</Card.Header>
              <Card.Body>
                <Form onSubmit={handleAssessFine}>
                  <Form.Group className="mb-3">
                    <Form.Label>รายการยืม</Form.Label>
                    <Form.Select
                      value={assessForm.loan_no}
                      onChange={(event) => setAssessForm((prev) => ({ ...prev, loan_no: event.target.value }))}
                      required
                    >
                      <option value="">-- เลือกรายการยืม --</option>
                      {loans.map((loan) => (
                        <option key={loan.loan_no} value={loan.loan_no}>
                          {loan.loan_no} - {loan.copy_code} - {loan.borrower_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row className="g-2">
                    <Col sm={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ประเภทค่าปรับ</Form.Label>
                        <Form.Select
                          value={assessForm.fine_reason}
                          onChange={(event) => setAssessForm((prev) => ({ ...prev, fine_reason: event.target.value }))}
                        >
                          <option value="overdue">เกินกำหนด</option>
                          <option value="damage">ชำรุด</option>
                          <option value="loss">สูญหาย</option>
                          <option value="other">อื่นๆ</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ยอดค่าปรับ</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={assessForm.fine_amount}
                          onChange={(event) => setAssessForm((prev) => ({ ...prev, fine_amount: event.target.value }))}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>ยอดยกเว้นเริ่มต้น</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={assessForm.waived_amount}
                      onChange={(event) => setAssessForm((prev) => ({ ...prev, waived_amount: event.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={assessForm.fine_note}
                      onChange={(event) => setAssessForm((prev) => ({ ...prev, fine_note: event.target.value }))}
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button type="submit" disabled={submittingAssess || loading}>
                      {submittingAssess ? "กำลังบันทึก..." : "บันทึกค่าปรับ"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="h-100">
              <Card.Header className="fw-semibold">รับชำระค่าปรับ</Card.Header>
              <Card.Body>
                <Form onSubmit={handlePayFine}>
                  <Form.Group className="mb-3">
                    <Form.Label>รายการค่าปรับ</Form.Label>
                    <Form.Select
                      value={payForm.fine_no}
                      onChange={(event) => setPayForm((prev) => ({ ...prev, fine_no: event.target.value }))}
                      required
                    >
                      <option value="">-- เลือกรายการค่าปรับ --</option>
                      {payableFines.map((fine) => (
                        <option key={fine.fine_no} value={fine.fine_no}>
                          {fine.fine_no} - {fine.borrower_name} - คงเหลือ {(Number(fine.fine_amount || 0) - Number(fine.paid_amount || 0) - Number(fine.waived_amount || 0)).toFixed(2)} บาท
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row className="g-2">
                    <Col sm={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ยอดรับชำระ</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={payForm.paid_amount}
                          onChange={(event) => setPayForm((prev) => ({ ...prev, paid_amount: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ยอดยกเว้นเพิ่ม</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={payForm.waived_amount}
                          onChange={(event) => setPayForm((prev) => ({ ...prev, waived_amount: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={payForm.fine_note}
                      onChange={(event) => setPayForm((prev) => ({ ...prev, fine_note: event.target.value }))}
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button type="submit" disabled={submittingPay || loading} variant="success">
                      {submittingPay ? "กำลังบันทึก..." : "บันทึกรับชำระ"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header className="fw-semibold">รายการค่าปรับล่าสุด</Card.Header>
          <Card.Body className="table-responsive">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Table hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Fine No</th>
                    <th>ผู้ยืม</th>
                    <th>หนังสือ</th>
                    <th>ยอดค่าปรับ</th>
                    <th>ชำระแล้ว</th>
                    <th>ยกเว้น</th>
                    <th>สถานะ</th>
                    <th>วันที่ประเมิน</th>
                  </tr>
                </thead>
                <tbody>
                  {fines.slice(0, 150).map((fine) => (
                    <tr key={fine.fine_id}>
                      <td>{fine.fine_no}</td>
                      <td>
                        <div>{fine.borrower_name}</div>
                        <small className="text-muted">{fine.borrower_idnumber}</small>
                      </td>
                      <td>{fine.title_name}</td>
                      <td>{Number(fine.fine_amount || 0).toFixed(2)}</td>
                      <td>{Number(fine.paid_amount || 0).toFixed(2)}</td>
                      <td>{Number(fine.waived_amount || 0).toFixed(2)}</td>
                      <td>{fineStatusLabel[fine.fine_status] || fine.fine_status}</td>
                      <td>{toDateTime(fine.assessed_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
